import React, { useState } from 'react';
import { CASE_STUDIES } from '../data/mockData';
import { ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { api } from '../api';
import { useNavigate } from 'react-router-dom';

const CaseStudies = () => {
  const [caseStudies, setCaseStudies] = React.useState(CASE_STUDIES);
  const navigate = useNavigate();

  React.useEffect(() => {
    api.getCaseStudies().then(data => {
      if (data && data.length > 0) setCaseStudies(data);
    }).catch(() => {});
  }, []);

  // Show only first 3 on homepage
  const featured = caseStudies.slice(0, 3);

  return (
    <section id="work" className="bg-[#FDFEFA] py-12 md:py-16">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-16">
        <motion.h2
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-[#242326] text-3xl md:text-5xl lg:text-[72px] font-light leading-tight tracking-tight font-['Inter'] text-center mb-10 md:mb-16"
        >
          Results we've delivered
        </motion.h2>

        <div className="space-y-6">
          {featured.map((study, index) => (
            <motion.div
              key={study.id}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-50px' }}
              transition={{ duration: 0.7, delay: index * 0.1 }}
              className="relative rounded-2xl overflow-hidden group cursor-pointer min-h-[380px] md:min-h-[500px]"
              onClick={() => navigate(`/work#${study.id}`)}
            >
              {/* Background Image */}
              <div className="absolute inset-0">
                <img
                  src={study.heroImage || study.image}
                  alt={study.title}
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20" />
              </div>

              {/* Dashboard Preview - hidden on mobile */}
              {study.dashboardImage && (
                <div className="absolute top-6 left-6 w-[180px] md:w-[280px] lg:w-[360px] rounded-xl overflow-hidden shadow-2xl transition-transform duration-500 group-hover:translate-y-[-8px] hidden sm:block border border-white/10">
                  <img
                    src={study.dashboardImage}
                    alt={`${study.client} dashboard`}
                    className="w-full h-auto"
                  />
                </div>
              )}

              {/* Content */}
              <div className="absolute bottom-0 left-0 right-0 sm:left-auto sm:right-0 p-6 md:p-12 lg:p-16 sm:max-w-[600px]">
                <h3 className="text-white text-2xl md:text-4xl lg:text-[44px] font-light leading-tight tracking-tight font-['Inter'] mb-8">
                  {study.title}
                </h3>

                <div className="flex gap-10 mb-8">
                  {study.stats.map((stat, i) => (
                    <div key={i}>
                      <div className="text-[#83BF42] text-3xl md:text-4xl font-bold font-['Inter']">
                        {stat.value}
                      </div>
                      <div className="text-white/70 text-sm font-['Lato'] mt-1">
                        {stat.label}
                      </div>
                    </div>
                  ))}
                </div>

                <button className="inline-flex items-center gap-2 text-white text-sm font-['Inter'] hover:text-[#83BF42] transition-colors group/link">
                  View the case study
                  <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                </button>
              </div>

              {/* Category Tag & Number */}
              <div className="absolute top-6 right-6 flex flex-col items-end gap-2">
                <span className="text-white/80 text-xs uppercase tracking-wider font-['Inter'] bg-white/10 backdrop-blur-sm px-3 py-1 rounded-full">
                  {study.category}
                </span>
                <span className="text-white/30 text-5xl md:text-6xl font-['Inter'] font-light">
                  _{String(index + 1).padStart(2, '0')}
                </span>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mt-10"
        >
          <button
            onClick={() => navigate('/work')}
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-[#242326] text-white text-[15px] font-['Inter'] font-light hover:bg-[#83BF42] transition-all duration-300 group"
          >
            View All Case Studies
            <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default CaseStudies;
